/**
 * @fileoverview ResendPhoneVerificationRenderer component for the Storyteller web application.
 * Renders a link to resend a phone verification code with a countdown timer.
 * Supports i18n translations and error handling.
 *
 * @example
 * // Basic usage
 * <ResendPhoneVerificationRenderer
 *   schema={{
 *     type: 'string',
 *     options: {
 *       text: 'Resend code',
 *       timeout: 60,
 *       pendingTimeoutText: 'Please wait {counter} seconds',
 *       nthPendingTimeoutPrefix: 'Haven\'t received the code? ',
 *     }
 *   }}
 *   path="phone.verification.code"
 *   handleChange={(path, value) => console.log(path, value)}
 * />
 */

import { ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { JSONFormContext } from '../../../../JSONFormContext'
import css from './ResendPhoneVerificationRenderer.module.css'

/**
 * @function ResendPhoneVerificationRenderer
 * @description Component for rendering a phone verification resend link with a countdown timer.
 * @param {ControlProps} props - The props for the ResendPhoneVerificationRenderer component.
 * @returns {JSX.Element} The ResendPhoneVerificationRenderer component.
 */
function ResendPhoneVerificationRenderer(props: ControlProps) {
    const { t } = useTranslation()
    const { clearErrors, onSubmit } = useContext(JSONFormContext)
    const { uischema } = props
    const { answer, timeout, pendingTimeoutText, nthPendingTimeoutPrefix } = uischema.options || {}

    const [state, setState] = useState<number>(timeout)
    const [sentCounter, setSentCounter] = useState<number>(0)

    useEffect(() => {
        let t: number
        if (state > 0) {
            t = window.setTimeout(() => {
                setState(state - 1)
            }, 1000)
        }

        return () => window.clearTimeout(t)
    }, [state, sentCounter])

    async function onClick() {
        clearErrors()
        onSubmit(answer)
        setSentCounter(sentCounter + 1)
        setState(30)
    }

    if (state === 0) {
        return (
            <button onClick={onClick} className={css.link}>
                {t(uischema.options.text)}
            </button>
        )
    }

    return (
        <span className={css.text}>
            {sentCounter > 0 && <span className={css.prefix}>{t(nthPendingTimeoutPrefix)} </span>}
            {t(pendingTimeoutText, {
                counter: state,
            })}
        </span>
    )
}

export const ResendPhoneVerificationTester = rankWith(3, uiTypeIs('ResendPhoneVerification'))

export default withJsonFormsControlProps(ResendPhoneVerificationRenderer)
